import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { useUsers } from '../../context/UsersContext';
import { useCheckTypes, useWorkflowStatus } from '../../hooks/useMasterData';
import AdvancedFilterDrawer from '../../components/AdvancedFilterDrawer';
import MonthYearPicker from '../../components/MonthYearPicker';
import SearchableDropdown from '../../components/SearchableDropdown';
import ColumnSelector from '../../components/ColumnSelector';
import Tooltip from '../../components/Tooltip';
import UserTimestamp from '../../components/UserTimestamp';
import { MdRestoreFromTrash } from 'react-icons/md';
import { Trash2, Info, RotateCcw } from 'lucide-react';
import { formatDateUS, formatDateRange, getCurrentMonthYear, formatMonthYear, getMonthRange } from '../../utils/dateUtils';
import { filterEmojis } from '../../utils/emojiFilter';
import { loadVisibleColumns, persistVisibleColumns } from '../../utils/columnPreferences';
import { useResizableTableColumns } from '../../hooks/useResizableTableColumns';
import { CellEllipsis, OverflowCell } from '../../components/CellEllipsis';
import { ResizableTh } from '../../components/ResizableTableHeader';
import '../Checks.css';

const CHECK_TRASH_COLUMN_PREFS_KEY = 'checkTrashTable';
const CHECK_TRASH_TABLE_WIDTHS_KEY = 'checkTrashTableColWidths';
const CHECK_TRASH_DEFAULT_COL_WIDTH_PX = {
  __spacer__: 40,
  depositDate: 92,
  checkNumber: 120,
  altCheckNumber: 110,
  payer: 130,
  batchDescription: 140,
  exchange: 90,
  checkType: 110,
  practiceCode: 100,
  locationCode: 100,
  totalAmount: 104,
  postedAmount: 100,
  remainingAmount: 104,
  otherAmount: 100,
  status: 110,
  workflowStatus: 120,
  assigneeName: 120,
  reporterName: 120,
  createdAt: 118,
  updatedAt: 118,
  action: 128
};

const CheckTrash = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const { data: checkTypes } = useCheckTypes(true);
  const { data: workflowStatuses } = useWorkflowStatus(true);

  const [checks, setChecks] = useState([]);
  const [allChecks, setAllChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('1');
  const [showPagination, setShowPagination] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPractice, setSelectedPractice] = useState('');
  const [practices, setPractices] = useState([]);

  const defaultAdvancedFilters = {
    statuses: [],
    practiceCodes: [],
    locationCodes: [],
    assigneeIds: [],
    excludeAssigneeIds: [],
    reporterIds: [],
    excludeReporterIds: [],
    checkTypes: [],
    workflowStatuses: [],
    checkNumber: '',
    batchNumber: '',
    payer: '',
    unknown: undefined,
    depositDateFrom: '',
    depositDateTo: '',
    receivedDateFrom: '',
    receivedDateTo: '',
    completedDateFrom: '',
    completedDateTo: '',
    month: null,
    year: null
  };
  const [advancedFilters, setAdvancedFilters] = useState(defaultAdvancedFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [sortField, setSortField] = useState('depositDate');
  const [sortDirection, setSortDirection] = useState('desc');

  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorTriggerRef = useRef(null);

  // Restore confirmation state
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [checkToRestore, setCheckToRestore] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);

  // Permanent delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [checkToDelete, setCheckToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(currentMonthYear.month);
  const [selectedYear, setSelectedYear] = useState(currentMonthYear.year);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  const { getUserName, getUserById } = useUsers();

  const allColumns = [
    { key: 'depositDate', label: 'Deposit Date' },
    { key: 'checkNumber', label: 'Check Number' },
    { key: 'altCheckNumber', label: 'Alt. Check Number' },
    { key: 'payer', label: 'Payer' },
    { key: 'batchDescription', label: 'Batch Description' },
    { key: 'exchange', label: 'Exchange' },
    { key: 'checkType', label: 'Check Type' },
    { key: 'practiceCode', label: 'Practice Code' },
    { key: 'locationCode', label: 'Location Code' },
    { key: 'totalAmount', label: 'Total Amount' },
    { key: 'postedAmount', label: 'Posted' },
    { key: 'remainingAmount', label: 'Remaining' },
    { key: 'otherAmount', label: 'Other Amount' },
    { key: 'status', label: 'Status' },
    { key: 'workflowStatus', label: 'Workflow Status' },
    { key: 'assigneeName', label: 'Assignee Name' },
    { key: 'reporterName', label: 'Reporter Name' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' },
    { key: 'action', label: 'Action', alwaysVisible: true },
  ];

  const defaultColumns = [
    'depositDate',
    'checkNumber',
    'payer',
    'exchange',
    'totalAmount',
    'postedAmount',
    'remainingAmount',
    'status',
    'updatedAt',
    'action',
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumns(
      CHECK_TRASH_COLUMN_PREFS_KEY,
      new Set(allColumns.map(c => c.key)),
      defaultColumns
    )
  );

  const checkTrashColumnOrder = useMemo(() => ['__spacer__', ...visibleColumns], [visibleColumns]);
  const { Colgroup, onResizeMouseDown, resetColumnWidths } = useResizableTableColumns({
    storageKey: CHECK_TRASH_TABLE_WIDTHS_KEY,
    columnIds: checkTrashColumnOrder,
    defaultWidths: CHECK_TRASH_DEFAULT_COL_WIDTH_PX,
    minWidth: 40
  });

  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const list = Array.isArray(response) ? response : (response?.items || []);
      setPractices(list.filter(p => p.isActive !== false));
    } catch (err) {
      console.error('Error fetching practices:', err);
    }
  };

  const fetchChecks = useCallback(async (page = 0) => {
    setLoading(true);
    setError(null);
    try {
      const searchParams = { page, size: 50 };

      const statuses = [];
      if (selectedStatus) statuses.push(selectedStatus);
      if (advancedFilters.statuses?.length) statuses.push(...advancedFilters.statuses);
      if (statuses.length) searchParams.statuses = [...new Set(statuses)];

      const practiceCodes = [];
      if (selectedPractice) practiceCodes.push(selectedPractice);
      if (advancedFilters.practiceCodes?.length) practiceCodes.push(...advancedFilters.practiceCodes);
      if (practiceCodes.length) searchParams.practiceCodes = [...new Set(practiceCodes)];

      if (advancedFilters.locationCodes?.length) searchParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      if (advancedFilters.assigneeIds?.length) searchParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      if (advancedFilters.excludeAssigneeIds?.length) searchParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      if (advancedFilters.reporterIds?.length) searchParams.reporterIds = [...new Set(advancedFilters.reporterIds)];
      if (advancedFilters.excludeReporterIds?.length) searchParams.excludeReporterIds = [...new Set(advancedFilters.excludeReporterIds)];
      if (advancedFilters.checkTypes?.length) searchParams.checkTypes = [...new Set(advancedFilters.checkTypes)];
      if (advancedFilters.workflowStatuses?.length) searchParams.workflowStatuses = [...new Set(advancedFilters.workflowStatuses)];
      if (advancedFilters.checkNumber?.trim()) searchParams.checkNumber = advancedFilters.checkNumber.trim().toUpperCase();
      if (advancedFilters.batchNumber?.trim()) searchParams.batchNumber = advancedFilters.batchNumber.trim();
      if (advancedFilters.payer?.trim()) searchParams.payer = advancedFilters.payer.trim();
      if (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) searchParams.unknown = advancedFilters.unknown;

      const hasDepositDateFilter = advancedFilters.depositDateFrom || advancedFilters.depositDateTo;
      const isAdvancedActive = (
        (advancedFilters.statuses?.length) ||
        (advancedFilters.practiceCodes?.length) ||
        (advancedFilters.locationCodes?.length) ||
        (advancedFilters.assigneeIds?.length) ||
        (advancedFilters.excludeAssigneeIds?.length) ||
        (advancedFilters.reporterIds?.length) ||
        (advancedFilters.excludeReporterIds?.length) ||
        (advancedFilters.checkTypes?.length) ||
        (advancedFilters.workflowStatuses?.length) ||
        advancedFilters.checkNumber?.trim() ||
        advancedFilters.batchNumber?.trim() ||
        advancedFilters.payer?.trim() ||
        (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
        hasDepositDateFilter ||
        advancedFilters.receivedDateFrom || advancedFilters.receivedDateTo ||
        advancedFilters.completedDateFrom || advancedFilters.completedDateTo
      );

      const monthRange = getMonthRange(selectedMonth, selectedYear);
      if (hasDepositDateFilter) {
        if (advancedFilters.depositDateFrom) searchParams.depositDateFrom = advancedFilters.depositDateFrom;
        if (advancedFilters.depositDateTo) searchParams.depositDateTo = advancedFilters.depositDateTo;
      } else if (!isAdvancedActive) {
        searchParams.depositDateFrom = monthRange.startDate;
        searchParams.depositDateTo = monthRange.endDate;
      }
      if (advancedFilters.receivedDateFrom) searchParams.receivedDateFrom = advancedFilters.receivedDateFrom;
      if (advancedFilters.receivedDateTo) searchParams.receivedDateTo = advancedFilters.receivedDateTo;
      if (advancedFilters.completedDateFrom) searchParams.completedDateFrom = advancedFilters.completedDateFrom;
      if (advancedFilters.completedDateTo) searchParams.completedDateTo = advancedFilters.completedDateTo;

      const response = await api.searchTrashChecksDashboard(searchParams);
      let list = response.items || [];
      const total = response.totalElements || 0;

      if (sortField && list.length > 0) {
        list = [...list].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          if (['depositDate', 'createdAt', 'updatedAt'].includes(sortField)) {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else if (['totalAmount', 'postedAmount', 'remainingAmount', 'otherAmount'].includes(sortField)) {
            aVal = aVal || 0;
            bVal = bVal || 0;
          } else {
            aVal = aVal || '';
            bVal = bVal || '';
          }
          if (sortDirection === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        });
      }

      setAllChecks(list);
      setTotalElements(total);
      const pages = response.totalPages || 0;
      setTotalPages(pages);
      setHasMore(page < pages - 1);
      setCurrentPage(page);
      setPageInputValue((page + 1).toString());
      setShowPagination(true);
    } catch (err) {
      console.error('Error fetching trash checks:', err);
      const msg = err?.message || err?.data?.message || 'Failed to load trash checks.';
      setError(msg);
      showError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, showError]);

  const prevFiltersRef = useRef({ selectedStatus, selectedPractice, advancedFilters: JSON.stringify(advancedFilters), selectedMonth, selectedYear });

  useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current.selectedStatus !== selectedStatus ||
      prevFiltersRef.current.selectedPractice !== selectedPractice ||
      prevFiltersRef.current.advancedFilters !== JSON.stringify(advancedFilters) ||
      prevFiltersRef.current.selectedMonth !== selectedMonth ||
      prevFiltersRef.current.selectedYear !== selectedYear;

    if (filtersChanged) {
      setShowPagination(false);
      if (currentPage !== 0) {
        setCurrentPage(0);
        setPageInputValue('1');
        prevFiltersRef.current = { selectedStatus, selectedPractice, advancedFilters: JSON.stringify(advancedFilters), selectedMonth, selectedYear };
        return;
      }
    }
    prevFiltersRef.current = { selectedStatus, selectedPractice, advancedFilters: JSON.stringify(advancedFilters), selectedMonth, selectedYear };
    fetchChecks(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus, selectedPractice, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear]);

  // Client-side search filter
  useEffect(() => {
    if (!searchTerm) { setChecks(allChecks); return; }
    const lower = searchTerm.toLowerCase().trim();
    setChecks(allChecks.filter(c =>
      (c.checkNumber && c.checkNumber.toLowerCase().includes(lower)) ||
      (c.payer && c.payer.toLowerCase().includes(lower)) ||
      String(c.totalAmount || '').includes(lower) ||
      String(c.postedAmount || '').includes(lower) ||
      String(c.remainingAmount || '').includes(lower)
    ));
  }, [searchTerm, allChecks]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handlePreviousPage = () => { if (currentPage > 0 && !loading) setCurrentPage(p => p - 1); };
  const handleNextPage = () => { if (hasMore && !loading) setCurrentPage(p => p + 1); };
  const handlePageInputChange = (e) => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setPageInputValue(e.target.value); };
  const handlePageInputKeyPress = (e) => { if (e.key === 'Enter') handleGoToPage(); };
  const handleGoToPage = () => {
    if (!pageInputValue || loading) return;
    const n = parseInt(pageInputValue, 10);
    const max = totalPages > 0 ? totalPages - 1 : 0;
    if (n >= 1 && n <= max + 1) setCurrentPage(n - 1);
    else setPageInputValue((currentPage + 1).toString());
  };

  useEffect(() => { setPageInputValue((currentPage + 1).toString()); }, [currentPage]);

  const pageSize = 50;
  const displayTotal = searchTerm ? checks.length : totalElements;
  const startIndex = searchTerm ? 1 : (currentPage * pageSize + 1);
  const endIndex = searchTerm ? checks.length : Math.min((currentPage + 1) * pageSize, totalElements);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  const getStatusClass = (status) => {
    const map = {
      'COMPLETED': 'status-complete',
      'UNDER_CLARIFICATIONS': 'status-clarification',
      'IN_PROGRESS': 'status-progress',
      'NOT_STARTED': 'status-not-started',
      'OVER_POSTED': 'status-over-posted'
    };
    return map[status] || 'status-not-started';
  };

  const formatStatus = (status) => {
    const map = { 'COMPLETED': 'Completed', 'UNDER_CLARIFICATIONS': 'Clarifications', 'IN_PROGRESS': 'In Progress', 'NOT_STARTED': 'Not Started', 'OVER_POSTED': 'Over Posted' };
    return map[status] || status;
  };

  const formatCheckType = (ct) => {
    if (!ct) return '';
    return checkTypes.find(x => x.code === ct)?.name || ct;
  };

  const hasAdvancedFilters = () => (
    (advancedFilters.statuses?.length) ||
    (advancedFilters.practiceCodes?.length) ||
    (advancedFilters.locationCodes?.length) ||
    (advancedFilters.assigneeIds?.length) ||
    (advancedFilters.excludeAssigneeIds?.length) ||
    (advancedFilters.reporterIds?.length) ||
    (advancedFilters.excludeReporterIds?.length) ||
    (advancedFilters.checkTypes?.length) ||
    (advancedFilters.workflowStatuses?.length) ||
    advancedFilters.checkNumber?.trim() ||
    advancedFilters.batchNumber?.trim() ||
    advancedFilters.payer?.trim() ||
    (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
    advancedFilters.depositDateFrom?.trim() ||
    advancedFilters.depositDateTo?.trim() ||
    advancedFilters.receivedDateFrom?.trim() ||
    advancedFilters.receivedDateTo?.trim() ||
    advancedFilters.completedDateFrom?.trim() ||
    advancedFilters.completedDateTo?.trim() ||
    advancedFilters.month || advancedFilters.year
  );

  const getDateRangeDisplay = () => {
    if (advancedFilters.startDate && advancedFilters.endDate) return formatDateRange(advancedFilters.startDate, advancedFilters.endDate);
    if (hasAdvancedFilters()) return 'Custom Range';
    return formatMonthYear(selectedMonth, selectedYear);
  };

  const handleClearAllFilters = () => {
    setAdvancedFilters(defaultAdvancedFilters);
    setSelectedStatus('');
    setSelectedPractice('');
    setSearchTerm('');
    setShowAdvancedFilters(false);
  };

  const totals = checks.reduce((acc, c) => ({
    totalAmount: acc.totalAmount + (c.totalAmount || 0),
    posted: acc.posted + (c.postedAmount || 0),
    remaining: acc.remaining + (c.remainingAmount || 0),
    other: acc.other + (c.otherAmount || 0)
  }), { totalAmount: 0, posted: 0, remaining: 0, other: 0 });

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="sort-arrow">{sortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Restore
  const handleRestoreClick = (check) => { setCheckToRestore(check); setShowRestoreConfirm(true); };
  const handleRestoreCancel = () => { setShowRestoreConfirm(false); setCheckToRestore(null); };
  const handleRestoreConfirm = async () => {
    if (!checkToRestore) return;
    setIsRestoring(true);
    try {
      await api.restoreTrashCheck(checkToRestore.checkId);
      showSuccess(`Check "${checkToRestore.checkNumber || 'Unknown'}" restored successfully`);
      setShowRestoreConfirm(false);
      setCheckToRestore(null);
      await fetchChecks(currentPage);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to restore check.';
      showError(msg);
    } finally {
      setIsRestoring(false);
    }
  };

  // Permanent delete
  const handleDeleteClick = (check) => { setCheckToDelete(check); setShowDeleteConfirm(true); };
  const handleDeleteCancel = () => { setShowDeleteConfirm(false); setCheckToDelete(null); };
  const handleDeleteConfirm = async () => {
    if (!checkToDelete) return;
    setIsDeleting(true);
    try {
      await api.permanentDeleteTrashCheck(checkToDelete.checkId);
      showSuccess(`Check "${checkToDelete.checkNumber || 'Unknown'}" permanently deleted`);
      setShowDeleteConfirm(false);
      setCheckToDelete(null);
      await fetchChecks(currentPage);
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to permanently delete check.';
      showError(msg);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="checks-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Check Trash</h1>
          <p className="page-subtitle">View and manage deleted checks. Restore or permanently remove them.</p>
        </div>
        <div className="header-actions">
          {!hasAdvancedFilters() && (
            <div className="date-range-container">
              <button className="date-range-btn" onClick={() => setShowMonthPicker(!showMonthPicker)}>
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={(m, y) => { setSelectedMonth(m); setSelectedYear(y); setShowMonthPicker(false); }}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasAdvancedFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">{getDateRangeDisplay()}</div>
            </div>
          )}
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by check number, payer, amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(filterEmojis(e.target.value))}
          />
        </div>
        <div className="filter-dropdowns">
          <SearchableDropdown
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'NOT_STARTED', label: 'Not Started' },
              { value: 'IN_PROGRESS', label: 'In Progress' },
              { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
              { value: 'COMPLETED', label: 'Completed' },
              { value: 'OVER_POSTED', label: 'Over Posted' }
            ]}
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="All Statuses"
            maxVisibleItems={5}
          />
          <SearchableDropdown
            options={[
              { value: '', label: 'All Practices' },
              ...practices.map(p => ({ value: p.code, label: p.name || p.code }))
            ]}
            value={selectedPractice}
            onChange={setSelectedPractice}
            placeholder="All Practices"
            maxVisibleItems={5}
          />
          <button
            className={`btn-advanced-filter ${hasAdvancedFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(true)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M7 10H17M11 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Advanced Filters
          </button>
        </div>
      </div>

      {(hasAdvancedFilters() || selectedStatus || selectedPractice || searchTerm) && (
        <div className="active-filters-section">
          <div className="active-filters-label">Active Filters:</div>
          <div className="active-filters-chips">
            {searchTerm && (
              <div className="filter-chip">
                <span>Search: {searchTerm}</span>
                <button className="chip-close-btn" onClick={() => setSearchTerm('')}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {selectedStatus && (
              <div className="filter-chip">
                <span>Status: {[
                  { value: 'NOT_STARTED', label: 'Not Started' },
                  { value: 'IN_PROGRESS', label: 'In Progress' },
                  { value: 'UNDER_CLARIFICATIONS', label: 'Under Clarifications' },
                  { value: 'COMPLETED', label: 'Completed' },
                  { value: 'OVER_POSTED', label: 'Over Posted' }
                ].find(s => s.value === selectedStatus)?.label || selectedStatus}</span>
                <button className="chip-close-btn" onClick={() => setSelectedStatus('')}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {selectedPractice && (
              <div className="filter-chip">
                <span>Practice: {practices.find(p => p.code === selectedPractice)?.name || selectedPractice}</span>
                <button className="chip-close-btn" onClick={() => setSelectedPractice('')}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {advancedFilters.statuses?.map(status => {
              const labels = { 'NOT_STARTED': 'Not Started', 'IN_PROGRESS': 'In Progress', 'UNDER_CLARIFICATIONS': 'Under Clarifications', 'COMPLETED': 'Completed', 'OVER_POSTED': 'Over Posted' };
              return (
                <div key={status} className="filter-chip">
                  <span>Status: {labels[status] || status}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, statuses: prev.statuses.filter(s => s !== status) }))}>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.practiceCodes?.map(pc => (
              <div key={pc} className="filter-chip">
                <span>Practice: {practices.find(p => p.code === pc)?.name || pc}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, practiceCodes: prev.practiceCodes.filter(x => x !== pc), locationCodes: [] }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.locationCodes?.map(lc => (
              <div key={lc} className="filter-chip">
                <span>Location: {lc}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, locationCodes: prev.locationCodes.filter(x => x !== lc) }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.assigneeIds?.map(id => (
              <div key={id} className="filter-chip">
                <span>Assignee: {getUserName(id)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, assigneeIds: prev.assigneeIds.filter(x => x !== id) }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeAssigneeIds?.map(id => (
              <div key={`exclude-assignee-${id}`} className="filter-chip filter-chip--exclude">
                <span>Assignee: Not {getUserName(id)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeAssigneeIds: prev.excludeAssigneeIds.filter(x => x !== id) }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.reporterIds?.map(id => (
              <div key={id} className="filter-chip">
                <span>Reporter: {getUserName(id)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, reporterIds: prev.reporterIds.filter(x => x !== id) }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporterIds?.map(id => (
              <div key={`exclude-reporter-${id}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(id)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeReporterIds: prev.excludeReporterIds.filter(x => x !== id) }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.checkTypes?.map(ct => {
              const item = checkTypes.find(x => x.code === ct);
              return (
                <div key={ct} className="filter-chip">
                  <span>Check Type: {item?.name || ct}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, checkTypes: prev.checkTypes.filter(x => x !== ct) }))}>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.workflowStatuses?.map(ws => {
              const item = workflowStatuses.find(x => x.code === ws);
              return (
                <div key={ws} className="filter-chip">
                  <span>Workflow: {item?.name || ws}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, workflowStatuses: prev.workflowStatuses.filter(x => x !== ws) }))}>
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.checkNumber && (
              <div className="filter-chip">
                <span>Check: {advancedFilters.checkNumber}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, checkNumber: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {advancedFilters.batchNumber && (
              <div className="filter-chip">
                <span>Batch: {advancedFilters.batchNumber}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, batchNumber: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {advancedFilters.payer && (
              <div className="filter-chip">
                <span>Payer: {advancedFilters.payer}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, payer: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {(advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) && (
              <div className="filter-chip">
                <span>Unknown: {advancedFilters.unknown === true ? 'True' : 'False'}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, unknown: undefined }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {(advancedFilters.depositDateFrom || advancedFilters.depositDateTo) && (
              <div className="filter-chip">
                <span>Deposit Date: {advancedFilters.depositDateFrom && advancedFilters.depositDateTo
                  ? formatDateRange(advancedFilters.depositDateFrom, advancedFilters.depositDateTo)
                  : (advancedFilters.depositDateFrom ? formatDateUS(advancedFilters.depositDateFrom) : formatDateUS(advancedFilters.depositDateTo))}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, depositDateFrom: '', depositDateTo: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {(advancedFilters.receivedDateFrom || advancedFilters.receivedDateTo) && (
              <div className="filter-chip">
                <span>Received Date: {advancedFilters.receivedDateFrom && advancedFilters.receivedDateTo
                  ? formatDateRange(advancedFilters.receivedDateFrom, advancedFilters.receivedDateTo)
                  : (advancedFilters.receivedDateFrom ? formatDateUS(advancedFilters.receivedDateFrom) : formatDateUS(advancedFilters.receivedDateTo))}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, receivedDateFrom: '', receivedDateTo: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            {(advancedFilters.completedDateFrom || advancedFilters.completedDateTo) && (
              <div className="filter-chip">
                <span>Completed Date: {advancedFilters.completedDateFrom && advancedFilters.completedDateTo
                  ? formatDateRange(advancedFilters.completedDateFrom, advancedFilters.completedDateTo)
                  : (advancedFilters.completedDateFrom ? formatDateUS(advancedFilters.completedDateFrom) : formatDateUS(advancedFilters.completedDateTo))}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, completedDateFrom: '', completedDateTo: '' }))}>
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
            <button className="clear-all-filters-btn" onClick={handleClearAllFilters}>Clear All</button>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      <div className="table-section">
        <div className="table-header">
          <div className="table-header-left">
            {totalElements > 0 && showPagination && (
              <div className="pagination-info">
                <button className="pagination-arrow" onClick={handlePreviousPage} disabled={currentPage === 0 || loading}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                <span className="pagination-text">{startIndex} - {endIndex}, of {displayTotal}</span>
                {totalPages > 1 && (
                  <div className="pagination-page-input">
                    <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>Page:</span>
                    <input
                      type="text"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onKeyPress={handlePageInputKeyPress}
                      onBlur={handleGoToPage}
                      style={{ width: '45px', padding: '2px 4px', border: '1px solid #e5e7eb', borderRadius: '4px', fontSize: '12px', textAlign: 'center', height: '24px' }}
                    />
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>of {totalPages}</span>
                  </div>
                )}
                <button className="pagination-arrow" onClick={handleNextPage} disabled={!hasMore || loading}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            )}
          </div>
          <div className="table-actions">
            <Tooltip text="Reset Column widths" position="top">
              <button
                type="button"
                className="icon-btn"
                onClick={resetColumnWidths}
                aria-label="Reset Column widths"
              >
                <RotateCcw size={18} strokeWidth={2} />
              </button>
            </Tooltip>
            <button ref={columnSelectorTriggerRef} className="icon-btn" onClick={() => setShowColumnSelector(!showColumnSelector)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="checks-table checks-table--col-resize">
            <Colgroup />
            <thead>
              <tr className="totals-row">
                <th>
                  <Tooltip text="Totals reflect only the rows currently displayed on this page" position="top">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      <Info size={16} style={{ color: '#374151', cursor: 'pointer', strokeWidth: 2.5 }} />
                      Totals:
                    </span>
                  </Tooltip>
                </th>
                {visibleColumns.map(colKey => {
                  switch (colKey) {
                    case 'totalAmount': return <th key={colKey}>{formatCurrency(totals.totalAmount)}</th>;
                    case 'postedAmount': return <th key={colKey}>{formatCurrency(totals.posted)}</th>;
                    case 'remainingAmount': return <th key={colKey}>{formatCurrency(totals.remaining)}</th>;
                    case 'otherAmount': return <th key={colKey}>{formatCurrency(totals.other)}</th>;
                    default: return <th key={colKey}></th>;
                  }
                })}
              </tr>
              <tr>
                <ResizableTh columnId="__spacer__" onResizeMouseDown={onResizeMouseDown} resizable={false} />
                {visibleColumns.map(colKey => {
                  switch (colKey) {
                    case 'depositDate': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('depositDate')}>Deposit Date <SortArrow field="depositDate" /></ResizableTh>;
                    case 'checkNumber': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('checkNumber')}>Check Number <SortArrow field="checkNumber" /></ResizableTh>;
                    case 'altCheckNumber': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Alt. Check Number</ResizableTh>;
                    case 'payer': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Payer</ResizableTh>;
                    case 'batchDescription': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Batch Description</ResizableTh>;
                    case 'exchange': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Exchange</ResizableTh>;
                    case 'checkType': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Check Type</ResizableTh>;
                    case 'practiceCode': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Practice Code</ResizableTh>;
                    case 'locationCode': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Location Code</ResizableTh>;
                    case 'totalAmount': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('totalAmount')}>Total Amount <SortArrow field="totalAmount" /></ResizableTh>;
                    case 'postedAmount': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('postedAmount')}>Posted <SortArrow field="postedAmount" /></ResizableTh>;
                    case 'remainingAmount': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('remainingAmount')}>Remaining <SortArrow field="remainingAmount" /></ResizableTh>;
                    case 'otherAmount': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('otherAmount')}>Other Amount <SortArrow field="otherAmount" /></ResizableTh>;
                    case 'status': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Status</ResizableTh>;
                    case 'workflowStatus': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Workflow Status</ResizableTh>;
                    case 'assigneeName': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Assignee Name</ResizableTh>;
                    case 'reporterName': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>Reporter Name</ResizableTh>;
                    case 'createdAt': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('createdAt')}>Created At <SortArrow field="createdAt" /></ResizableTh>;
                    case 'updatedAt': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} className="sortable" onClick={() => handleSort('updatedAt')}>Updated At <SortArrow field="updatedAt" /></ResizableTh>;
                    case 'action': return <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown} style={{ textAlign: 'right' }}>Actions</ResizableTh>;
                    default: return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loading && checks.length === 0 ? (
                <tr><td colSpan={visibleColumns.length + 1} style={{ textAlign: 'center', padding: '40px' }}>Loading trash checks...</td></tr>
              ) : checks.length === 0 ? (
                <tr><td colSpan={visibleColumns.length + 1} style={{ textAlign: 'center', padding: '40px' }}>No deleted checks found</td></tr>
              ) : (
                checks.map(check => (
                  <tr key={check.checkId}>
                    <td></td>
                    {visibleColumns.map(colKey => {
                      switch (colKey) {
                        case 'depositDate':
                          return <td key={colKey}><CellEllipsis text={check.depositDate ? formatDateUS(check.depositDate) : ''} /></td>;
                        case 'checkNumber':
                          return (
                            <td key={colKey}>
                              <OverflowCell tooltipText={check.checkNumber || ''}>
                                <button
                                  type="button"
                                  className="link-btn"
                                  onClick={() => navigate(`/admin/check-trash/${check.checkId}`)}
                                >
                                  {check.checkNumber || ''}
                                </button>
                              </OverflowCell>
                            </td>
                          );
                        case 'altCheckNumber':
                          return <td key={colKey}><CellEllipsis text={check.altCheckNumber || ''} /></td>;
                        case 'payer':
                          return <td key={colKey}><CellEllipsis text={check.payer || ''} /></td>;
                        case 'batchDescription':
                          return <td key={colKey}><CellEllipsis text={check.batchDescription || ''} /></td>;
                        case 'exchange':
                          return <td key={colKey}><CellEllipsis text={check.exchange || ''} /></td>;
                        case 'checkType':
                          return <td key={colKey}><CellEllipsis text={formatCheckType(check.checkType)} /></td>;
                        case 'practiceCode':
                          return <td key={colKey}><CellEllipsis text={check.practiceCode || ''} /></td>;
                        case 'locationCode':
                          return <td key={colKey}><CellEllipsis text={check.locationCode || ''} /></td>;
                        case 'totalAmount':
                          return <td key={colKey}><CellEllipsis text={formatCurrency(check.totalAmount)} /></td>;
                        case 'postedAmount':
                          return <td key={colKey}><CellEllipsis text={formatCurrency(check.postedAmount)} /></td>;
                        case 'remainingAmount':
                          return <td key={colKey}><CellEllipsis text={formatCurrency(check.remainingAmount)} /></td>;
                        case 'otherAmount':
                          return <td key={colKey}><CellEllipsis text={formatCurrency(check.otherAmount)} /></td>;
                        case 'status':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getStatusClass(check.status)}`}>
                                <CellEllipsis text={formatStatus(check.status)} />
                              </span>
                            </td>
                          );
                        case 'workflowStatus':
                          return <td key={colKey}><CellEllipsis text={check.workflowStatus || 'N/A'} /></td>;
                        case 'assigneeName':
                          return <td key={colKey}><CellEllipsis text={getUserName(check.assigneeId)} /></td>;
                        case 'reporterName':
                          return <td key={colKey}><CellEllipsis text={getUserName(check.reporterId)} /></td>;
                        case 'createdAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp userId={check.createdBy} dateTime={check.createdAt} action="created" getUserName={getUserName} getUserById={getUserById} />
                            </td>
                          );
                        case 'updatedAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp userId={check.updatedBy} dateTime={check.updatedAt} action="updated" getUserName={getUserName} getUserById={getUserById} />
                            </td>
                          );
                        case 'action':
                          return (
                            <td key={colKey} style={{ textAlign: 'right', paddingRight: '16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'flex-end' }}>
                                <Tooltip text="Restore check" position="top">
                                  <button
                                    className="btn-icon"
                                    onClick={() => handleRestoreClick(check)}
                                    style={{ color: '#10b981', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                                  >
                                    <MdRestoreFromTrash size={18} />
                                  </button>
                                </Tooltip>
                                <Tooltip text="Permanently delete" position="top">
                                  <button
                                    className="btn-icon delete"
                                    onClick={() => handleDeleteClick(check)}
                                    style={{ color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </Tooltip>
                              </div>
                            </td>
                          );
                        default: return null;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <AdvancedFilterDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onApplyFilters={setAdvancedFilters}
        onResetFilters={() => setAdvancedFilters(defaultAdvancedFilters)}
        isTrashPage={true}
      />

      {showColumnSelector && (
        <ColumnSelector
          availableColumns={allColumns}
          visibleColumns={visibleColumns}
          defaultColumns={defaultColumns}
          onChange={(cols) => {
            setVisibleColumns(cols);
            persistVisibleColumns(CHECK_TRASH_COLUMN_PREFS_KEY, cols);
          }}
          onClose={() => setShowColumnSelector(false)}
          triggerRef={columnSelectorTriggerRef}
        />
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreConfirm && (
        <div className="modal-overlay" onClick={handleRestoreCancel}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#111827' }}>Restore Check</h3>
            <p className="modal-message">
              Are you sure you want to restore check <strong>"{checkToRestore?.checkNumber || 'Unknown'}"</strong>? It will be moved back to the active checks list.
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleRestoreCancel} disabled={isRestoring}>Cancel</button>
              <button
                className="btn-save"
                onClick={handleRestoreConfirm}
                disabled={isRestoring}
                style={{ background: '#10b981' }}
              >
                {isRestoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={handleDeleteCancel}>
          <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600', color: '#dc2626' }}>Permanently Delete Check</h3>
            <p className="modal-message">
              Are you sure you want to <strong>permanently delete</strong> check <strong>"{checkToDelete?.checkNumber || 'Unknown'}"</strong>? <span style={{ color: '#dc2626', fontWeight: '600' }}>This action cannot be undone.</span>
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleDeleteCancel} disabled={isDeleting}>Cancel</button>
              <button
                className="btn-save"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                style={{ background: '#dc2626' }}
              >
                {isDeleting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckTrash;
